<template>
  <div class="seminar-elearnings-fi">
    <grid-container-fi v-if="eventFacets && lmsData && !isEditMode">
      <div class="fi-col-span-full">
        <div class="seminar-elearnings-fi__facets">
          <facets-menu-fi
            centered
            :facets="eventFacets"
            :columns-max="4"
            @change="facetChanged"
            @clear-all-facets="clearAllFacets"
          />
        </div>
      </div>
    </grid-container-fi>

    <template v-if="eventsFiltered?.length && !isEditMode">
      <div
        v-for="(event, index) in eventsFiltered"
        :key="event.eventId"
        ref="eventItem"
        class="seminar-elearnings-fi__event"
      >
        <seminar-elearning-fi
          :event="event"
          :dark="!(index % 2)"
        />
      </div>
    </template>

    <grid-container-fi v-else>
      <div class="seminar-assignments-fi__no-results">
        <richtext-fi
          v-if="fields.noResultsElearning"
          :html-content="fields.noResultsElearning"
        />
      </div>
    </grid-container-fi>
  </div>
</template>

<script setup lang="ts">
import { RichtextFi } from 'atoms';
import { computed, ref, useTemplateRef, watch } from 'vue';

import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import SeminarElearningFi from '@/components/seminar-elearnings-fi/seminar-elearning-fi/SeminarElearningFi.vue';
import { useFields } from '@/composables/fields';
import { useSmoothScroll } from '@/composables/smoothScroll';

defineOptions({ name: 'SeminarElearningsFi' });

const props = withDefaults(
  defineProps<{
    lmsData?: object | null; // TODO
  }>(),
  { lmsData: null },
);

const emit = defineEmits<{
  'filters-changed': [];
}>();

const fields = useFields('SeminarsOverviewFiFields');
const { smoothScrollToElement } = useSmoothScroll();

const eventItems = useTemplateRef('eventItem');

// TODO: any
const events = ref<any[] | null>(null);
const activeFilters = ref<any[]>([]);
const filteredEvents = ref<any[] | null>(null);
const eventFacets = ref<any[] | null>(null);

const eventsFiltered = computed(() => filteredEvents.value ?? events.value);

const getProxySrc = (url: string) => {
  if (!url) return null;
  if (APP_ENVIRONMENT !== 'PROD' && APP_ENVIRONMENT !== 'UAT') {
    return url.replace('https://testfischerwerke.avendoo.de/api/v1/media', '_lms-media-int_');
  }
  return url.replace('https://fischeracademy.com/api/v1/media', '_lms-media-prod_');
};

const setFacets = () => {
  const facets: any[] = [];

  events.value?.forEach((event, eventIndex) => {
    const { eventId } = event;

    if (event.classifications) {
      event.classifications.forEach((facet: any, classificationIndex: number) => {
        const { key, value } = facet;
        const facetIndex = facets.findIndex((e) => e.name === key);

        if (facets.length && facetIndex > -1) {
          const facetElement = facets[facetIndex];
          const elementIndex = facetElement.elements.findIndex((e: any) => e.name === value);

          if (elementIndex > -1) {
            facetElement.elements[elementIndex].eventIds.push(eventId);
            facetElement.elements[elementIndex].count += 1;
          } else {
            facets[facetIndex].elements.push({
              facetId: Number(`${eventIndex}${classificationIndex}`),
              eventIds: [eventId],
              name: facet.value,
              selected: false,
              count: 1,
            });
          }
        } else {
          facets.push({
            multiselect: true,
            name: facet.key,
            elements: [
              {
                facetId: Number(`${eventIndex}${classificationIndex}`),
                eventIds: [eventId],
                name: facet.value,
                count: 1,
                selected: false,
              },
            ],
          });
        }
      });
    }
  });

  eventFacets.value = JSON.parse(JSON.stringify(facets));
};

const setEvents = () => {
  const eventsList: any[] = [];

  if (props.lmsData) {
    (props.lmsData as any).assignments.assignment.forEach((el: any) => {
      const assignmentId = el.assignment.assignmentId[0].id;
      const { title, teaser, description } = el;
      const link = el.deeplink?.href;
      const image = el.picture?.href && getProxySrc(el.picture.href);
      const classifications = el.classifications?.classification;
      const location = { type: 'online' };

      eventsList.push({
        eventId: assignmentId,
        assignmentId,
        location,
        title,
        teaser,
        description,
        link,
        image,
        classifications,
      });
    });
  }

  events.value = eventsList;
  setFacets();
};

watch(
  () => props.lmsData,
  (newLmsData, oldLmsData) => {
    if (newLmsData !== oldLmsData) {
      setEvents();
    }
  },
  { immediate: true },
);

const setUrlFilters = (filters: Record<string, any> = {}) => {
  Object.entries(filters).forEach(([name, value]) => {
    const oldFacet = eventFacets.value?.find((facet) => facet.name === name);
    if (!oldFacet) {
      console.error('Could not find old e-learning facet to restore', name);
      return;
    }
    const element = oldFacet.elements.find((item: any) => item.name === value);
    if (!element) {
      console.error('Could not find e-learning element', name, value);
      return;
    }
    facetChanged({ name, value: true, selected: true, element });
  });
};

const facetChanged = (facet: any) => {
  if (facet) {
    if (facet.value) {
      activeFilters.value.push(facet.element);
    } else {
      let index;
      if (facet.element?.name) {
        index = activeFilters.value.findIndex((filter) => filter.name === facet.element.name);
      } else if (facet.name) {
        index = activeFilters.value.indexOf(facet.element);
      } else {
        console.error('Could not unselect facet', facet);
        return;
      }
      if (index === -1) {
        console.error('Did not find facet', JSON.stringify(facet));
        return;
      }
      activeFilters.value.splice(index, 1);
    }
  }

  filterEvents();
  emit('filters-changed');
};

const filterEvents = () => {
  const newFilteredEvents: any[] = [];

  events.value?.forEach((event) => {
    let isActive = true;

    activeFilters.value.forEach((activeFilter) => {
      let f = false;
      activeFilter.eventIds.forEach((id: string) => {
        if (id === event.eventId) {
          f = true;
        }
      });
      if (!f) {
        isActive = false;
      }
    });

    if (isActive) {
      newFilteredEvents.push(event);
    }
  });

  filteredEvents.value = newFilteredEvents;
  updateFacets();
};

const updateFacets = () => {
  eventFacets.value?.forEach((facet) => {
    facet.elements.forEach((element: any) => {
      let counter = 0;
      filteredEvents.value?.forEach((event) => {
        element.eventIds.forEach((eventId: string) => {
          if (eventId === event.eventId) {
            counter += 1;
          }
        });
      });
      element.selected = activeFilters.value.includes(element);
      element.count = counter;
      element.disabled = counter === 0;
    });
  });
};

const clearAllFacets = () => {
  filteredEvents.value = null;
  activeFilters.value = [];
  setFacets();
  emit('filters-changed');
};

const scrollTo = (id: string, duration = 0) => {
  if (!id || !eventsFiltered.value?.length) return;
  const index = eventsFiltered.value.findIndex((e) => e.eventId === id);
  const el = index !== -1 ? eventItems.value?.[index] : null;
  if (el) {
    smoothScrollToElement(el, { duration });
  }
};

defineExpose({ scrollTo, setUrlFilters, activeFilters, eventFacets });
</script>

<style scoped src="./seminar-elearnings-fi" />
